import React from "react";
import { cn } from "@/utils/utils";

export interface ClickableLinkProps {
  /** The URL to navigate to when right-clicking or cmd/ctrl+clicking */
  href: string;
  /** Optional click handler for left-click. If not provided, navigates to href */
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  className?: string;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  target?: string;
  rel?: string;
}

export const ClickableLink: React.FC<ClickableLinkProps> = ({
  href,
  onClick,
  children,
  className,
  stopPropagation = true,
  preventDefault = !!onClick,
  target,
  rel
}) => {
  const handleMouseDown = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Only prevent default for left-click (button 0)
    // This allows right-click and middle-click to work normally
    if (e.button === 0 && preventDefault) {
      e.preventDefault();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if it's a modified click (Cmd/Ctrl + click, middle click, etc.)
    const isModifiedClick =
      e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0;

    if (stopPropagation) {
      e.stopPropagation();
    }

    if (isModifiedClick) {
      // Let the browser handle modified clicks (open in new tab)
      return;
    }

    // Handle regular left-click
    if (preventDefault) {
      e.preventDefault();
    }

    if (onClick) {
      onClick(e);
    }
    // If no onClick provided and preventDefault is false, browser will navigate
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      className={cn("cursor-pointer", className)}
      target={target}
      rel={rel || (target === "_blank" ? "noopener noreferrer" : undefined)}
    >
      {children}
    </a>
  );
};

export default ClickableLink;
